import { apiRequest } from '../../api';
import appConfig from '../../config/appConfig';
import { useState, useCallback, useEffect } from 'react';
import { Build as BuildIcon, Route as RouteIcon, Clear as ClearIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import { createCirclePolygon, createNetworkServiceArea, validateServiceArea } from '../../utils/distanceUtils';
import './Tools.css';

function Tools({ 
    mapRef, 
    setViewState, 
    onRoutingStateChange, 
    onRouteDataChange,
    onDistanceSearchStateChange,
    onDistanceBufferChange,
    setShowLayerControl,
    setShowSearch
}) {
    const [showTools, setShowTools] = useState(false);

    // Routing state
    const [routingState, setRoutingState] = useState({
        isActive: false,
        step: null, // 'origin', 'destination', 'complete'
        origin: null,
        destination: null
    });
    const [routeData, setRouteData] = useState(null);

    // Distance search state
    const [distanceSearchState, setDistanceSearchState] = useState({
        isActive: false,
        center: null,
        radius: 2, // Default 2km radius
        loading: false
    });
    const [distanceBuffer, setDistanceBuffer] = useState(null);

    // Notify parent when routing state changes
    useEffect(() => {
        if (onRoutingStateChange) {
            onRoutingStateChange(routingState);
        }
    }, [routingState, onRoutingStateChange]);

    // Notify parent when route data changes
    useEffect(() => {
        if (onRouteDataChange) {
            onRouteDataChange(routeData);
        }
    }, [routeData, onRouteDataChange]);

    // Notify parent when distance search state changes
    useEffect(() => {
        if (onDistanceSearchStateChange) {
            onDistanceSearchStateChange(distanceSearchState);
        }
    }, [distanceSearchState, onDistanceSearchStateChange]);

    // Notify parent when distance buffer changes
    useEffect(() => {
        if (onDistanceBufferChange) {
            onDistanceBufferChange(distanceBuffer);
        }
    }, [distanceBuffer, onDistanceBufferChange]);

    // Close other panels when tools is opened
    useEffect(() => {
        if (showTools) {
            if (setShowLayerControl) setShowLayerControl(false);
            if (setShowSearch) setShowSearch(false);
        }
    }, [showTools, setShowLayerControl, setShowSearch]);

    // Pan map to selected location
    const panToLocation = useCallback((lng, lat) => {
        if (setViewState) {
            setViewState(prev => ({
                ...prev,
                longitude: lng,
                latitude: lat,
                zoom: Math.max(prev.zoom, 15), // Ensure minimum zoom level
                transitionDuration: 1000 // Smooth transition
            }));
        }
    }, [setViewState]);

    // Routing functions
    const calculateRoute = async (origin, destination) => {
        try {
            const url = `${appConfig.api.routingBaseURL}/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`;
            const response = await apiRequest(url);
            const data = await response.json();

            if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
                const route = data.routes[0];
                return {
                    geometry: route.geometry,
                    distance: route.distance,
                    duration: route.duration,
                    origin,
                    destination
                };
            } else {
                throw new Error(data.message || 'No route found');
            }
        } catch (error) {

            alert('خطا در محاسبه مسیر: ' + error.message);
            return null;
        }
    };

    const handleRoutingClick = useCallback(async (e) => {
        if (!e || !e.lngLat) {

            return;
        }

        const { lng, lat } = e.lngLat;
        const clickPoint = { lng, lat };

        // Pan to selected location
        panToLocation(lng, lat);

        if (routingState.step === 'origin') {
            setRoutingState(prev => ({
                ...prev,
                step: 'destination',
                origin: clickPoint
            }));
        } else if (routingState.step === 'destination') {
            setRoutingState(prev => ({
                ...prev,
                step: 'complete',
                destination: clickPoint
            }));

            // Calculate route
            const route = await calculateRoute(routingState.origin, clickPoint);
            if (route) {
                setRouteData(route);
                setRoutingState(prev => ({
                    ...prev,
                    isActive: false,
                    step: null
                }));

                // Pan to show the entire route
                if (route.geometry && route.geometry.coordinates) {
                    const coords = route.geometry.coordinates;
                    const bounds = coords.reduce((bounds, coord) => {
                        return [
                            [Math.min(bounds[0][0], coord[0]), Math.min(bounds[0][1], coord[1])],
                            [Math.max(bounds[1][0], coord[0]), Math.max(bounds[1][1], coord[1])]
                        ];
                    }, [[coords[0][0], coords[0][1]], [coords[0][0], coords[0][1]]]);

                    // Calculate center and zoom to fit route
                    const centerLng = (bounds[0][0] + bounds[1][0]) / 2;
                    const centerLat = (bounds[0][1] + bounds[1][1]) / 2;

                    setViewState(prev => ({
                        ...prev,
                        longitude: centerLng,
                        latitude: centerLat,
                        zoom: 13, // Good zoom level to see the route
                        transitionDuration: 1500
                    }));
                }
            }
        }
    }, [routingState.step, routingState.origin, panToLocation, setViewState]);

    const startRouting = useCallback(() => {
        setRoutingState({
            isActive: true,
            step: 'origin',
            origin: null,
            destination: null
        });
        setShowTools(false);
    }, []);

    const clearRoute = useCallback(() => {
        setRouteData(null);
        setRoutingState({
            isActive: false,
            step: null,
            origin: null,
            destination: null
        });
    }, []);

    // Distance search functions
    const createCirclePolygon = (center, radiusKm) => {
        const points = 64;
        const coords = [];
        const earthRadius = 6371; // Earth's radius in km

        for (let i = 0; i <= points; i++) {
            const angle = (i * 360) / points;
            const angleRad = (angle * Math.PI) / 180;

            const lat = center.lat + (radiusKm / earthRadius) * (180 / Math.PI) * Math.cos(angleRad);
            const lng = center.lng + (radiusKm / earthRadius) * (180 / Math.PI) * Math.sin(angleRad) / Math.cos(center.lat * Math.PI / 180);

            coords.push([lng, lat]);
        }

        return coords;
    };

    const handleDistanceSearchClick = useCallback(async (e) => {
        if (!e || !e.lngLat) {

            return;
        }

        const { lng, lat } = e.lngLat;
        const clickPoint = { lng, lat };

        // Pan to selected location
        panToLocation(lng, lat);

        // Show loading state
        setDistanceSearchState(prev => ({
            ...prev,
            isActive: false,
            center: clickPoint,
            loading: true
        }));

        try {
            // Create network-based service area using OSRM

            const serviceAreaPolygon = await createNetworkServiceArea(clickPoint, distanceSearchState.radius, 'driving');

            // Validate the service area accuracy

            const validation = await validateServiceArea(clickPoint, serviceAreaPolygon, distanceSearchState.radius, 'driving');

            const bufferData = {
                center: clickPoint,
                radius: distanceSearchState.radius,
                polygon: serviceAreaPolygon,
                type: 'network', // Mark as network-based
                validation: validation
            };

            setDistanceBuffer(bufferData);

        } catch (error) {

            // Fallback to circular buffer if network calculation fails

            const circularPolygon = createCirclePolygon(clickPoint, distanceSearchState.radius);

            const bufferData = {
                center: clickPoint,
                radius: distanceSearchState.radius,
                polygon: circularPolygon,
                type: 'circular' // Mark as fallback
            };

            setDistanceBuffer(bufferData);
        } finally {
            // Clear loading state
            setDistanceSearchState(prev => ({
                ...prev,
                loading: false
            }));
        }

    }, [distanceSearchState.radius, panToLocation]);

    const startDistanceSearch = useCallback(() => {
        // Clear any existing buffer
        setDistanceBuffer(null);

        setDistanceSearchState(prev => ({
            ...prev,
            isActive: true,
            center: null
        }));
        setShowTools(false);
    }, []);

    const clearDistanceSearch = useCallback(() => {
        setDistanceBuffer(null);
        setDistanceSearchState({
            isActive: false,
            center: null,
            radius: distanceSearchState.radius // Keep the radius setting
        });
    }, [distanceSearchState.radius]);

    const updateSearchRadius = useCallback((newRadius) => {
        setDistanceSearchState(prev => ({
            ...prev,
            radius: newRadius
        }));

        // If there's an existing buffer, update it with new radius
        if (distanceBuffer && distanceBuffer.center) {
            const bufferPolygon = createCirclePolygon(distanceBuffer.center, newRadius);
            setDistanceBuffer(prev => ({
                ...prev,
                radius: newRadius,
                polygon: bufferPolygon
            }));
        }
    }, [distanceBuffer]);

    // Expose click handlers to parent
    Tools.handleRoutingClick = handleRoutingClick;
    Tools.handleDistanceSearchClick = handleDistanceSearchClick;

    return (
        <>
            {/* Tools Toggle Button */}
            <button
                onClick={() => setShowTools(!showTools)}
                className={`tools-toggle-button ${showTools ? 'active' : ''}`}
                title="ابزارها"
            >
                <BuildIcon />
            </button>

            {/* Tools Panel */}
            {showTools && (
                <div className="tools-panel">
                    <div className="tools-panel-title">
                        ابزارهای نقشه
                    </div>

                    {/* Routing Tool */}
                    <div
                        onClick={startRouting}
                        className={`tool-item routing ${routingState.isActive ? 'active' : 'inactive'}`}
                    >
                        <div className={`tool-item-text routing ${routingState.isActive ? 'active' : 'inactive'}`}>
                            مسیریابی
                        </div>
                        <RouteIcon 
                            className={`tool-item-icon routing ${routingState.isActive ? 'active' : 'inactive'}`}
                            style={{ fontSize: '16px' }}
                        />
                    </div>

                    {/* Distance Search Tool */}
                    <div
                        onClick={startDistanceSearch}
                        className={`tool-item distance-search ${distanceSearchState.isActive ? 'active' : 'inactive'}`}
                    >
                        <div className={`tool-item-text distance-search ${distanceSearchState.isActive ? 'active' : 'inactive'}`}>
                            یافتن خانه ها از فاصله مشخص
                        </div>
                        <LocationIcon 
                            className={`tool-item-icon distance-search ${distanceSearchState.isActive ? 'active' : 'inactive'}`}
                            style={{ fontSize: '16px' }}
                        />
                    </div>

                    {/* Distance Radius Control */}
                    <div className="distance-radius-control">
                        <div className="distance-radius-label">
                            شعاع جستجو: {distanceSearchState.radius} کیلومتر
                        </div>
                        <input
                            type="range"
                            min="0.5"
                            max="10"
                            step="0.5"
                            value={distanceSearchState.radius}
                            onChange={(e) => updateSearchRadius(parseFloat(e.target.value))}
                            className="distance-radius-slider"
                        />

                        {/* Test buttons for debugging */}
                        <div className="test-buttons-container">
                            <button
                                onClick={async () => {
                                    const testPoint = { lng: 51.4262, lat: 35.7643 }; // Tehran center

                                    try {
                                        const serviceAreaPolygon = await createNetworkServiceArea(testPoint, distanceSearchState.radius, 'driving');

                                        // Validate the test service area
                                        const validation = await validateServiceArea(testPoint, serviceAreaPolygon, distanceSearchState.radius, 'driving');

                                        const bufferData = {
                                            center: testPoint,
                                            radius: distanceSearchState.radius,
                                            polygon: serviceAreaPolygon,
                                            type: 'network',
                                            validation: validation
                                        };
                                        setDistanceBuffer(bufferData);

                                    } catch (error) {

                                    }
                                }}
                                className="test-button network"
                            >
                                تست شبکه
                            </button>
                            <button
                                onClick={() => {
                                    const testPoint = { lng: 51.4262, lat: 35.7643 }; // Tehran center
                                    const bufferPolygon = createCirclePolygon(testPoint, distanceSearchState.radius);
                                    const bufferData = {
                                        center: testPoint,
                                        radius: distanceSearchState.radius,
                                        polygon: bufferPolygon,
                                        type: 'circular'
                                    };
                                    setDistanceBuffer(bufferData);

                                }}
                                className="test-button circular"
                            >
                                تست دایره
                            </button>
                        </div>
                    </div>

                    {/* Clear Route Button */}
                    {routeData && (
                        <div
                            onClick={clearRoute}
                            className="clear-button route"
                        >
                            <div className="clear-button-text route">
                                حذف مسیر
                            </div>
                            <ClearIcon 
                                className="clear-button-icon route"
                                style={{ fontSize: '16px' }}
                            />
                        </div>
                    )}

                    {/* Clear Distance Search Button */}
                    {distanceBuffer && (
                        <div
                            onClick={clearDistanceSearch}
                            className="clear-button distance"
                        >
                            <div className="clear-button-text distance">
                                حذف جستجوی فاصله
                            </div>
                            <ClearIcon 
                                className="clear-button-icon distance"
                                style={{ fontSize: '16px' }}
                            />
                        </div>
                    )}

                    {/* Routing Instructions */}
                    {routingState.isActive && (
                        <div className="instructions-container routing">
                            <div className="instructions-text routing">
                                {routingState.step === 'origin' && 'مبدأ را روی نقشه انتخاب کنید'}
                                {routingState.step === 'destination' && 'مقصد را روی نقشه انتخاب کنید'}
                                {routingState.step === 'complete' && 'مسیر محاسبه شد'}
                            </div>
                        </div>
                    )}

                    {/* Distance Search Instructions */}
                    {distanceSearchState.isActive && (
                        <div className="instructions-container distance-search">
                            <div className="instructions-text distance-search">
                                روی نقشه کلیک کنید تا منطقه قابل دسترس در شعاع {distanceSearchState.radius} کیلومتری را ببینید
                            </div>
                        </div>
                    )}

                    {/* Loading State */}
                    {distanceSearchState.loading && (
                        <div className="instructions-container loading">
                            <div className="instructions-text loading">
                                در حال محاسبه منطقه قابل دسترس...
                            </div>
                            <div className="instructions-text loading-detail">
                                این فرآیند ممکن است تا 30 ثانیه طول بکشد
                            </div>
                            {/* Simple progress bar */}
                            <div className="progress-bar-container">
                                <div className="progress-bar"></div>
                            </div>
                        </div>
                    )}

                    {/* Distance Search Result Info */}
                    {distanceBuffer && !distanceSearchState.loading && (
                        <div className={`distance-result-info ${distanceBuffer.type}`}>
                            <div className={`distance-result-main-text ${distanceBuffer.type}`}>
                                {distanceBuffer.type === 'network' 
                                    ? `منطقه قابل دسترس در ${distanceBuffer.radius} کیلومتر (بر اساس شبکه جاده‌ای) 🛣️`
                                    : `منطقه دایره‌ای در شعاع ${distanceBuffer.radius} کیلومتر (فاصله مستقیم) ⭕`
                                }
                            </div>

                            {/* Validation Results */}
                            {distanceBuffer.validation && (
                                <div className={`distance-result-validation ${distanceBuffer.type}`}>
                                    <div className="validation-stat">دقت: {((distanceBuffer.validation.validPoints / (distanceBuffer.validation.validPoints + distanceBuffer.validation.invalidPoints)) * 100).toFixed(0)}%</div>
                                    <div className="validation-stat">میانگین فاصله: {distanceBuffer.validation.averageDistance.toFixed(2)} کیلومتر</div>
                                    <div className="validation-stat">محدوده: {distanceBuffer.validation.minDistance.toFixed(2)} - {distanceBuffer.validation.maxDistance.toFixed(2)} کیلومتر</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default Tools;
